<script setup lang="ts">
defineOptions({ name: 'ShoppingListPage' })

import { LazyShareShoppingListModal } from '#components'
import draggable from 'vuedraggable'
import PrintList from '~/components/PrintList.vue'
import { SHOPPING_LIST_SHOW_AISLES_CACHE_KEY } from '~/stores/cache'

// Métadonnées de la page de liste d'épicerie
useHead({
  title: 'Liste d\'épicerie - Glouton',
  meta: [
    { name: 'description', content: 'Gérez votre liste d\'épicerie intelligemment. Organisez vos courses et ne manquez jamais d\'ingrédients.' },
    { property: 'og:title', content: 'Liste d\'épicerie - Glouton' },
    { property: 'og:description', content: 'Gérez votre liste d\'épicerie intelligemment. Organisez vos courses et ne manquez jamais d\'ingrédients.' },
  ],
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/shopping-list',
    fr: '/liste-epicerie',
  },
})

definePageMeta({
  name: 'ShoppingListPage',
  keepalive: true,
  key: 'shopping-list',
})

const userStore = useUserStore()
const baseStore = useBaseStore()
const cacheStore = useCacheStore()
const config = useRuntimeConfig()
const localePath = useLocalePath()
const route = useRoute()
const modalStore = useModalStore()
const router = useRouter()
const { maybeRequestReview } = useInAppReview()
const overlay = useOverlayWithAd()
const eventBusStore = useEventBusStore()
const toast = useToast()

const { isExtraUser, showExtraFeatureToast } = useExtraFeature()

function readShowAislesSetting(): boolean {
  if (!isExtraUser.value)
    return false

  const cached = cacheStore.getCache(SHOPPING_LIST_SHOW_AISLES_CACHE_KEY)
  return cached !== undefined ? Boolean(cached) : true
}

const showAislesSetting = ref(false)

watch(isExtraUser, () => {
  showAislesSetting.value = readShowAislesSetting()
}, { immediate: true })

watch(showAislesSetting, (val) => {
  if (isExtraUser.value)
    cacheStore.setCache(SHOPPING_LIST_SHOW_AISLES_CACHE_KEY, val)
})

const canShowAislesOnItems = computed(() => baseStore.showAisles && showAislesSetting.value)

const settingsOpen = ref(false)

const { getAisleMeta, getDisplayAisleMeta, getSortedAisles, OTHER_AISLE_ID } = useStoreAisles()

const lists = ref([])
const listsLoading = ref({})
const itemsLoading = ref([])
const itemsEditing = ref([])
const selectedItems = ref([])
const createSublistLoading = ref(false)
const newSublistLabel = ref('')
const newItemLabel = ref({})
const editItemLabel = ref({})
const isInitialLoading = ref(false)
const isAllListsLoading = ref(false)
const loadingListIds = ref<Record<number, boolean>>({})
const promoModalOpen = ref(false)
const promoDetailsId = ref(false)
const showHelp = ref(false)
const allowMultiselect = ref(false)
const openNewList = ref(false)
const printListRef = ref(null)
const sharingStatus = ref(null)
const isUpdatingAisles = ref(false)
const aisleItemsLoading = ref([])
const aislePopoverOpen = ref({})

type FetchLoadingScope = 'initial' | 'global' | 'list' | 'none'

function isListFrozen(listId: number): boolean {
  return isAllListsLoading.value || !!loadingListIds.value[listId]
}

function setListLoading(listId: number, loading: boolean) {
  if (loading) {
    loadingListIds.value = { ...loadingListIds.value, [listId]: true }
  }
  else {
    const { [listId]: _, ...rest } = loadingListIds.value
    loadingListIds.value = rest
  }
}

function startLoadingScope(scope: FetchLoadingScope, listId?: number) {
  if (scope === 'initial' && lists.value.length === 0) {
    isInitialLoading.value = true
  }
  else if (scope === 'global') {
    isAllListsLoading.value = true
  }
  else if (scope === 'list' && listId != null) {
    setListLoading(listId, true)
  }
}

function stopLoadingScope(scope: FetchLoadingScope, listId?: number) {
  if (scope === 'initial') {
    isInitialLoading.value = false
  }
  else if (scope === 'global') {
    isAllListsLoading.value = false
  }
  else if (scope === 'list' && listId != null) {
    setListLoading(listId, false)
  }
}

// Computed pour les actions du menu Actions
const dropdownItems = computed(() => {
  const actions = [
    {
      label: 'Ajouter une sous-liste',
      icon: 'i-mdi-plus',
      onSelect: () => {
        openNewList.value = true
      },
    },
    {
      label: allowMultiselect.value ? 'Désactiver les actions groupées' : 'Activer les actions groupées',
      icon: allowMultiselect.value ? 'i-mdi-checkbox-multiple-marked-outline' : 'i-mdi-checkbox-multiple-marked-outline',
      onSelect: () => {
        if (allowMultiselect.value) {
          // Si on désactive, vider la liste des éléments sélectionnés
          selectedItems.value = []
        }
        allowMultiselect.value = !allowMultiselect.value
      },
    },
    {
      label: 'Supprimer les articles expirés',
      icon: 'mdi-delete-clock-outline',
      onSelect: () => fetchItems(true, 'all', 'global'),
    },
    { type: 'separator' as const },
    {
      label: 'Imprimer',
      icon: 'i-mdi-printer',
      onSelect: () => printList(),
    },
    {
      label: 'Rafraîchir',
      icon: 'i-mdi-refresh',
      onSelect: () => fetchItems(false, 'none', 'global'),
    },
  ]

  if (baseStore.showAisles) {
    actions.push(buildSortByAisleMenuItem())
  }

  return actions
})

// Computed pour les actions de sélection multiple
const multiselectItems = computed(() => {
  const actions = [
    {
      label: `Supprimer les articles sélectionnés`,
      icon: 'i-mdi-delete-forever',
      onSelect: () => deleteItems(),
    },
  ]

  // Ajouter l'option "Déplacer vers" seulement s'il y a plus d'une liste
  if (lists.value.length > 1) {
    actions.push({
      label: 'Déplacer vers',
      icon: 'i-mdi-arrow-right-bold',
      children: lists.value.map(list => ({
        label: list.label,
        onSelect: () => {
          moveSelectedItems(list.id)
        },
      })),
    })
  }

  return actions
})

const tabs = computed(() => {
  return [
    {
      label: 'Liste d\'épicerie',
      to: localePath({ name: 'ShoppingListPage' }),
      active: route.name === 'ShoppingListPage___fr',
      extra: false,
    },
    {
      label: 'Planificateur',
      to: localePath({ name: 'MealPlannerPage' }),
      active: route.name === 'MealPlannerPage___fr',
      extra: true,
    },
    {
      label: 'Boîte de recettes',
      to: localePath({ name: 'RecipeBoxPage' }),
      active: route.name === 'RecipeBoxPage___fr',
      extra: false,
    },
  ]
})

function isFlyerOfferExpired(element) {
  if (!element?.expires)
    return false

  const parsed = Date.parse(element.expires)
  return !Number.isNaN(parsed) && parsed <= Date.now()
}

function getItemActions(element) {
  const actions = []

  if (element.flyer_item_id && !isFlyerOfferExpired(element)) {
    actions.push({
      label: 'Afficher dans la circulaire',
      icon: 'material-symbols:eye-tracking-outline-sharp',
      onSelect: () => goToFlyer(element),
    })
    actions.push({ type: 'separator' as const })
  }

  actions.push({
    label: 'Modifier',
    icon: 'i-mdi-pencil',
    onSelect: () => {
      itemsEditing.value.push(element.id)
      editItemLabel.value[element.id] = element.label
    },
  })

  if (lists.value.length > 1) {
    actions.push({
      label: 'Déplacer vers',
      icon: 'i-mdi-arrow-right-bold',
      children: lists.value
        .filter(list => list.id !== element.list_id)
        .map(list => ({
          label: list.label,
          onSelect: () => {
            moveItemToList(element, list.id)
          },
        })),
    })
  }

  actions.push({
    label: 'Supprimer',
    icon: 'i-mdi-delete-forever',
    onSelect: () => {
      deleteItem(element)
    },
  })

  return actions
}

function isAisleItemLoading(itemId) {
  return aisleItemsLoading.value.includes(itemId)
}

function moveItemToList(element, targetListId) {
  // Trouver la liste actuelle de l'item
  const currentList = lists.value.find(list =>
    list.items.some(listItem => listItem.id === element.id),
  )

  if (!currentList) {
    console.error('Liste actuelle introuvable pour l\'item.')
    return
  }

  // Trouver la liste cible
  const targetList = lists.value.find(list => list.id === targetListId)
  if (!targetList) {
    console.error('Liste cible introuvable.')
    return
  }

  // Retirer l'item de la liste actuelle
  currentList.items = currentList.items.filter(listItem => listItem.id !== element.id)

  // Ajouter l'item à la liste cible
  targetList.items.push({
    ...element,
    list_id: targetListId, // Mettre à jour l'ID de la liste de l'item
  })

  refreshRanks()
  syncLists()

  // Toast de succès
  toast.add({
    title: 'Élément déplacé',
    description: 'L\'élément a été déplacé vers la nouvelle liste.',
    color: 'success',
    icon: 'i-lucide-check-circle',
  })
}

async function goToFlyer(item) {
  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/get-flyer-data`, {
      method: 'GET',
      query: { item_id: item.id },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    console.log(response)
    if (response.success == 1) {
      console.log('success')
      router.push(localePath({
        name: 'FlyerPage',
        params: {
          id: response.flyer_id,
          slug: response.slug,
        },
        query: {
          item_id: response.flyer_item_id,
        },
      }))
      console.log('redirected')
    }
    else if (response.success == 0) {
      // Handle error - could add toast notification here
      console.error(response.message)
    }
  }
  catch (err) {
    console.error('Error getting flyer data:', err)
  }
}

function refreshShoppingListOnEnter() {
  if (!userStore.user)
    return

  const loadingScope: FetchLoadingScope = lists.value.length === 0 ? 'initial' : 'none'
  fetchItems(false, 'none', loadingScope)
}

onActivated(() => {
  refreshShoppingListOnEnter()
})

// Écouter l'événement de rafraîchissement de la liste
watch(() => eventBusStore.lastEvent, (event) => {
  if (event === 'refresh-shopping-list' && userStore.user) {
    fetchItems(false, 'none', 'global')
  }
})

// Watcher pour recharger la liste quand le statut utilisateur change
watch(() => userStore.user, (newUser, oldUser) => {
  // Si l'utilisateur passe de null/false à un objet utilisateur
  if (newUser && !oldUser) {
    fetchItems(false, 'none')
  }
}, { immediate: false })

async function updateItem(item) {
  const label = editItemLabel.value[item.id]?.trim()
  if (!label)
    return

  item.label = label

  try {
    itemsLoading.value.push(item.id)
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/sync-item`, {
      method: 'GET',
      params: { item },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      const idx = itemsEditing.value.indexOf(item.id)
      if (idx !== -1)
        itemsEditing.value.splice(idx, 1)
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    const idx = itemsLoading.value.indexOf(item.id)
    if (idx !== -1)
      itemsLoading.value.splice(idx, 1)
  }
}

function listChange() {
  fetchItems(false, 'none', 'none')
}

function refreshRanks() {
  for (let i = 0; i < lists.value.length; i++) {
    for (let k = 0; k < lists.value[i].items.length; k++) {
      lists.value[i].items[k].rank = k + 1
      lists.value[i].items[k].list_id = lists.value[i].id
    }
  }
}

function slash(element) {
  element.slashed = element.slashed === 1 ? 0 : 1
  syncItem(element)
}

async function updateQuantity(element, action) {
  const list_item_id = element.id
  if (action === 'plus')
    element.qty += 1
  else if (action === 'minus')
    element.qty -= 1
  else return false

  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/update-quantity`, {
      method: 'POST',
      body: { list_item_id, action },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      userStore.user.grocery_list_items = response.lists
    }
  }
  catch (err) {
    console.error(err)
  }
}

async function syncItem(item) {
  try {
    await $fetch(`${config.public.apiUrl}/grocery-list/sync-item`, {
      method: 'GET',
      params: { item },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
  }
  catch (err) {
    console.error(err)
  }
}

async function fetchItems(
  clean_expired: boolean,
  clean_list_id: number | 'none' | 'all' = 'none',
  loadingScope: FetchLoadingScope = 'initial',
) {
  const listId = typeof clean_list_id === 'number' ? clean_list_id : undefined
  startLoadingScope(loadingScope, listId)

  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/fetch-items`, {
      method: 'GET',
      params: { clean_expired, clean_list_id },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    lists.value = response.lists
    userStore.user.grocery_list_items = response.user_lists
    sharingStatus.value = response.sharing_status || null
  }
  catch (err) {
    console.error(err)
  }
  finally {
    stopLoadingScope(loadingScope, listId)
  }
}

async function updateAisles() {
  if (isUpdatingAisles.value) {
    return
  }

  isUpdatingAisles.value = true
  isAllListsLoading.value = true

  try {
    await $fetch(`${config.public.apiUrl}/grocery-list/update-aisles`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    await fetchItems(false, 'none', 'none')
  }
  catch (err) {
    console.error(err)
  }
  finally {
    isAllListsLoading.value = false
    isUpdatingAisles.value = false
  }
}

function buildSortByAisleMenuItem(listId) {
  const extra = isExtraUser.value

  return {
    label: 'Classer par allée',
    icon: 'i-mdi-sort-variant',
    extraBadge: true,
    class: extra ? undefined : 'opacity-60',
    trailingIcon: extra ? undefined : 'i-mdi-lock',
    onSelect: () => {
      if (!extra) {
        showExtraFeatureToast('Le classement par allée est disponible uniquement avec un abonnement Extra.')
        return
      }
      sortByAisle(listId)
    },
  }
}

async function sortByAisle(listId?: number) {
  const isGlobal = listId == null

  if (isGlobal) {
    if (isAllListsLoading.value) {
      return
    }
    isAllListsLoading.value = true
  }
  else {
    if (loadingListIds.value[listId]) {
      return
    }
    setListLoading(listId, true)
  }

  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/sort-by-aisle`, {
      method: 'GET',
      params: {
        list_id: listId ?? 'all',
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    if (response.success === 1) {
      lists.value = response.lists
      toast.add({
        title: 'Liste classée',
        description: 'Les articles ont été classés par allée.',
        color: 'success',
        icon: 'i-lucide-check-circle',
        timeout: 3000,
      })
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    if (isGlobal) {
      isAllListsLoading.value = false
    }
    else {
      setListLoading(listId, false)
    }
  }
}

async function saveItemAisle(element, aisleId) {
  if (!element?.id || isAisleItemLoading(element.id)) {
    return
  }

  aislePopoverOpen.value[element.id] = false
  aisleItemsLoading.value.push(element.id)

  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/save-item-aisle`, {
      method: 'POST',
      body: {
        list_item_id: element.id,
        aisle_id: aisleId,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    if (response?.success === 1) {
      element.aisle_id = response.item?.aisle_id ?? aisleId
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    aisleItemsLoading.value = aisleItemsLoading.value.filter(id => id !== element.id)
  }
}

function printList() {
  printListRef.value?.printList()
  plausible('print_list')
}

async function createSublist() {
  if (!newSublistLabel.value.trim())
    return
  createSublistLoading.value = true
  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/create-sublist`, {
      method: 'GET',
      params: { label: newSublistLabel.value },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      lists.value = response.lists
      newSublistLabel.value = ''
      createSublistLoading.value = false // Fermer le popover après création réussie
      openNewList.value = false

      // Toast de succès
      const toast = useToast()
      toast.add({
        title: 'Sous-liste créée',
        description: `La sous-liste «${response.lists[response.lists.length - 1]?.label}» a été créée avec succès.`,
        color: 'success',
        icon: 'i-lucide-check-circle',
        timeout: 3000,
      })
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    createSublistLoading.value = false
  }
}

async function addItem(e, listId) {
  const label = newItemLabel.value[listId]?.trim()
  if (!label)
    return

  try {
    listsLoading.value[listId] = true
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/add-item`, {
      method: 'GET',
      params: { list_id: listId, label },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      const item = response.item
      const list = lists.value.find(l => l.id === listId)
      if (list)
        list.items.push(item)
      newItemLabel.value[listId] = ''

      if (response.aisle_pending) {
        detectItemAisle(item)
      }

      void maybeRequestReview('groceryAdd')
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    listsLoading.value[listId] = false
  }
}

async function detectItemAisle(item) {
  if (!item?.id)
    return

  aisleItemsLoading.value.push(item.id)

  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/detect-item-aisle`, {
      method: 'POST',
      body: {
        list_item_id: item.id,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    if (response.success === 1 && response.item) {
      item.aisle_id = response.item.aisle_id ?? null
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    aisleItemsLoading.value = aisleItemsLoading.value.filter(id => id !== item.id)
  }
}

function getListMoveActions(list) {
  const listIndex = lists.value.findIndex(l => l.id === list.id)
  const isAtTop = listIndex === 0
  const isAtBottom = listIndex === lists.value.length - 1

  return [
    {
      label: 'En haut',
      icon: 'i-mdi-arrow-collapse-up',
      disabled: isAtTop,
      onSelect: () => moveList(list, 'top'),
    },
    {
      label: 'Vers le haut',
      icon: 'i-mdi-arrow-up',
      disabled: isAtTop,
      onSelect: () => moveList(list, 'up'),
    },
    {
      label: 'Vers le bas',
      icon: 'i-mdi-arrow-down',
      disabled: isAtBottom,
      onSelect: () => moveList(list, 'down'),
    },
    {
      label: 'En bas',
      icon: 'i-mdi-arrow-collapse-down',
      disabled: isAtBottom,
      onSelect: () => moveList(list, 'bottom'),
    },
  ]
}

async function moveList(list, direction) {
  setListLoading(list.id, true)

  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/move-list`, {
      method: 'POST',
      body: {
        list_id: list.id,
        direction,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    if (response.success === 1) {
      lists.value = response.lists
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    setListLoading(list.id, false)
  }
}

function getListActions(list) {
  const actions = []

  if (baseStore.showAisles) {
    actions.push(buildSortByAisleMenuItem(list.id))
  }

  if (lists.value.length > 1) {
    actions.push({
      label: 'Déplacer',
      icon: 'i-mdi-arrow-up-down-bold',
      children: getListMoveActions(list),
    })
  }

  const deleteChildren = [
    {
      label: 'Vider la sous-liste',
      icon: 'i-mdi-playlist-remove',
      onSelect: () => clearList(list),
    },{
      label: 'Supprimer les articles expirés',
      icon: 'mdi-delete-clock-outline',
      onSelect: () => fetchItems(true, list.id, 'list'),
    },
    
  ]
  if (list.default_list === 0) {
    deleteChildren.push({
      label: 'Supprimer la sous-liste',
      icon: 'i-mdi-delete-forever-outline',
      onSelect: () => deleteList(list),
    })
  }

  actions.push({
    label: 'Supprimer',
    icon: 'i-mdi-delete-outline',
    children: deleteChildren,
  })

  return actions
}

async function clearList(list) {
  if (!window.confirm('Voulez-vous vraiment vider cette sous-liste?'))
    return

  setListLoading(list.id, true)

  try {
    const response: any = await $fetch(`${config.public.apiUrl}/grocery-list/clear-list`, {
      method: 'GET',
      params: { list_id: list.id },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      list.items = []
      userStore.user.grocery_list_items = response.lists

      toast.add({
        title: 'Sous-liste vidée',
        description: `La sous-liste «${list.label}» a été vidée avec succès.`,
        color: 'success',
        icon: 'i-lucide-check-circle',
        timeout: 3000,
      })
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    setListLoading(list.id, false)
  }
}

async function deleteList(list) {
  if (!window.confirm('Voulez-vous vraiment supprimer cette sous-liste?'))
    return

  setListLoading(list.id, true)

  try {
    const response: any = await $fetch(`${config.public.apiUrl}/grocery-list/delete-list`, {
      method: 'GET',
      params: { list_id: list.id },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      lists.value = lists.value.filter(l => l.id !== list.id)
      userStore.user.grocery_list_items = response.lists

      toast.add({
        title: 'Sous-liste supprimée',
        description: `La sous-liste «${list.label}» a été supprimée avec succès.`,
        color: 'success',
        icon: 'i-lucide-check-circle',
        timeout: 3000,
      })
    }
  }
  catch (err) {
    console.error(err)
  }
  finally {
    setListLoading(list.id, false)
  }
}

async function deleteItem(item) {
  if (!window.confirm('Voulez-vous vraiment supprimer cet article?'))
    return
  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/delete-item`, {
      method: 'GET',
      params: { item },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      lists.value.forEach((list) => {
        list.items = list.items.filter(i => i.id !== item.id)
      })
      userStore.user.grocery_list_items = response.lists
    }
  }
  catch (err) {
    console.error(err)
  }
}

function checkChange() {
  refreshRanks()
  syncLists()
}

let syncListsInFlight: Promise<void> | null = null
let syncListsPending = false

async function syncLists() {
  if (syncListsInFlight) {
    syncListsPending = true
    return syncListsInFlight
  }

  syncListsInFlight = (async () => {
    do {
      syncListsPending = false

      try {
        await $fetch(`${config.public.apiUrl}/grocery-list/sync-lists`, {
          method: 'POST',
          body: { lists: lists.value },
          headers: {
            Authorization: `Bearer ${userStore.access_token}`,
          },
        })
      }
      catch (err) {
        console.error(err)
      }
    } while (syncListsPending)
  })().finally(() => {
    syncListsInFlight = null
  })

  return syncListsInFlight
}

// Fonction pour déplacer un élément vers une autre liste
function moveItem(item, targetListId) {
  // Trouver la liste actuelle de l'item
  const currentList = lists.value.find(list =>
    list.items.some(listItem => listItem.id === item.id),
  )

  if (!currentList) {
    return
  }

  // Trouver la liste cible
  const targetList = lists.value.find(list => list.id === targetListId)
  if (!targetList) {
    return
  }

  // Retirer l'item de la liste actuelle
  currentList.items = currentList.items.filter(listItem => listItem.id !== item.id)

  // Ajouter l'item à la liste cible
  targetList.items.push({
    ...item,
    list_id: targetListId,
  })

  refreshRanks()
  syncLists()
}

// Fonction pour déplacer plusieurs éléments sélectionnés
function moveSelectedItems(listId) {
  // Trouver la liste cible
  const targetList = lists.value.find(list => list.id === listId)
  if (!targetList) {
    return
  }

  // Itérer sur chaque ID sélectionné
  selectedItems.value.forEach((itemId) => {
    // Trouver la liste actuelle contenant l'item
    const currentList = lists.value.find(list =>
      list.items.some(listItem => listItem.id === itemId),
    )

    if (!currentList) {
      return
    }

    // Trouver l'item à déplacer
    const item = currentList.items.find(listItem => listItem.id === itemId)
    if (!item) {
      return
    }

    // Retirer l'item de la liste actuelle
    currentList.items = currentList.items.filter(listItem => listItem.id !== itemId)

    // Ajouter l'item à la liste cible
    targetList.items.push({
      ...item,
      list_id: listId,
    })
  })

  // Rafraîchir les rangs et synchroniser les listes
  refreshRanks()
  syncLists()
  selectedItems.value = []
}

// Fonction pour supprimer plusieurs éléments sélectionnés
async function deleteItems() {
  if (!window.confirm('Voulez-vous vraiment supprimer ces articles?'))
    return

  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/delete-items`, {
      method: 'GET',
      params: {
        'items[]': selectedItems.value,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (response.success === 1) {
      lists.value.forEach((list) => {
        list.items = list.items.filter(item => !selectedItems.value.includes(item.id))
      })
      userStore.user.grocery_list_items = response.lists
      selectedItems.value = []
    }
  }
  catch (err) {
    console.error(err)
  }
}

function openShareModal() {
  const modal = overlay.create(LazyShareShoppingListModal)
  modal.open()
}
</script>

<template>
  <div id="ShoppingListPage" class="relative">
    <TopNav
      title="Liste d'épicerie"
      description="Une liste d'épicerie classable et éditable qui vous suit partout. Fini les oublis!"
      access="Un compte gratuit offre un accès complet et illimité à cet outil."
      :show-help-button="userStore.user"
      :help-status="showHelp"
      class="max-w-7xl mx-auto"
      :tabs="tabs"
      @help-button-click="showHelp = !showHelp"
    />
    <UContainer id="ShoppingListPage-anchor" class="px-0">
      <div v-if="userStore.user && !showHelp" class="py-6">
        <ClientOnly>
          <div class=" mx-auto lg:px-0 space-y-6 print:hidden">
            <PromoBanner class="mb-6 mx-4 lg:mx-0" message-type="message_shopping_list" />
            <div class="flex space-x-2 px-4 sm:px-0 overflow-x-auto">
              <!-- Menu d'actions -->
              <UDropdownMenu
                :items="dropdownItems"
                size="lg"
                :content="{
                  align: 'center',
                  side: 'bottom',
                  sideOffset: 1,
                }"
                class="group"
              >
                <UButton
                  label="Actions"
                  trailing-icon="i-mdi-menu-down"
                  color="secondary"
                  :ui="{
                    trailingIcon: 'size-6 shrink-0 group-data-[state=open]:rotate-180 transition-transform duration-200',
                  }"
                />
                <template #item-label="{ item }">
                  {{ item.label }}
                  <span v-if="item.extraBadge" class="extra-badge-gold">Extra</span>
                </template>
              </UDropdownMenu>

              <UPopover
                v-if="baseStore.showAisles"
                v-model:open="settingsOpen"
                :content="{
                  align: 'center',
                  side: 'bottom',
                  sideOffset: 1,
                }"
              >
                <UButton
                  icon="i-mdi-cog"
                  color="secondary"
                  :variant="solid"
                  aria-label="Réglages"
                  :ui="{
                    leadingIcon: 'size-5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90',
                  }"
                  class="group"
                />

                <template #content>
                  <div class="p-1 w-64">
                    <div class="px-2 py-2">
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-sm">Afficher les allées</span>
                        <USwitch
                          v-model="showAislesSetting"
                          :disabled="!isExtraUser"
                          size="sm"
                        />
                      </div>
                      <p v-if="!isExtraUser" class="text-xs text-gray-400 mt-1">
                        Le classement intelligent par allée est réservé aux membres
                        <ULink :to="localePath({ name: 'ExtraPage' })" class="text-primary underline">Extra</ULink>.
                      </p>
                    </div>
                  </div>
                </template>
              </UPopover>

              <UButton
                icon="i-mdi-user-multiple"
                color="primary"
                @click="openShareModal"
              />

              <!-- Menu d'actions pour sélection multiple -->
              <UDropdownMenu
                v-if="selectedItems.length > 0"
                :items="multiselectItems"
                size="lg"
                :content="{
                  align: 'end',
                  side: 'bottom',
                  sideOffset: 1,
                }"
                class="group"
              >
                <UButton
                  :label="`${selectedItems.length} ${selectedItems.length === 1 ? 'sélectionné' : 'sélectionnés'}`"
                  trailing-icon="i-mdi-menu-down"
                  color="primary"
                  :ui="{
                    trailingIcon: 'size-6 shrink-0 group-data-[state=open]:rotate-180 transition-transform duration-200',
                  }"
                />
              </UDropdownMenu>
            </div>

            <div v-if="openNewList" class="px-4 sm:px-0 flex gap-2 items-center">
              <UInput
                v-model="newSublistLabel"
                :loading="createSublistLoading"
                :disabled="createSublistLoading"
                size="sm"
                class="flex-1"
                placeholder="Nom de la sous-liste..."
                @keydown.enter="createSublist()"
              />
              <UButton
                size="sm"
                icon="i-mdi-check"
                color="primary"
                :loading="createSublistLoading"
                :disabled="createSublistLoading"
                @click="createSublist()"
              />
              <UButton
                size="sm"
                icon="i-mdi-close"
                variant="ghost"
                @click="openNewList = false; newSublistLabel = ''"
              />
            </div>

            <!-- Note de collaboration -->
            <div v-if="sharingStatus?.is_joined && sharingStatus?.host_name" class="mx-4 sm:mx-0">
              <UAlert

                class=""
                color="tertiary"
                variant="soft"
                :description="`Vous collaborez sur la liste de ${sharingStatus.host_name}`"
                icon="i-mdi-account-multiple"
              />
            </div>

            <!-- Chargement initial -->
            <div v-if="isInitialLoading && lists.length === 0" class="w-full my-12 flex items-center justify-center">
              <UIcon name="i-mdi-loading" class="size-12 text-poivre-400 animate-spin" />
            </div>

            <!-- Affichage des listes -->
            <div v-else-if="lists.length > 0" id="list_to_print" class="space-y-0 lg:space-y-6">
              <template v-for="(list, index) in lists" :key="list.id">
                <div class="border border-gray-200 sm:rounded-lg overflow-hidden print:block">
                  <div class="flex justify-between items-center pl-4 pr-0.5 py-2 bg-white border-b border-gray-200">
                    <h2 class="font-semibold text-poivre truncate">
                      {{ list.label }}
                    </h2>
                    <div class="inline-flex size-8 shrink-0 items-center justify-center print:hidden">
                      <UIcon
                        v-if="isListFrozen(list.id)"
                        name="i-mdi-loading"
                        class="size-6 text-poivre-400 animate-spin"
                        aria-busy="true"
                        aria-label="Chargement"
                      />
                      <UDropdownMenu v-else :items="getListActions(list)" class="flex size-8 items-center justify-center">
                        <UButton
                          icon="i-mdi-dots-vertical"
                          variant="ghost"
                          color="secondary"
                          size="xs"
                          class="size-6 p-0 justify-center"
                          :ui="{ leadingIcon: 'size-5 shrink-0' }"
                        />
                        <template #item-label="{ item }">
                          {{ item.label }}
                          <span v-if="item.extraBadge" class="extra-badge-gold">Extra</span>
                        </template>
                      </UDropdownMenu>
                    </div>
                  </div>

                  <div class="relative">
                    <div v-if="list.items.length === 0" class="p-3 text-poivre-400 bg-poivre-50 print:hidden">
                      Cette sous-liste est vide.
                    </div>

                    <!-- Draggable Items -->
                    <draggable
                      :list="list.items"
                      item-key="id"
                      group="items"
                      handle=".handle"
                      ghost-class="ghost-class"
                      class="divide-y divide-gray-200"
                      @end="checkChange"
                    >
                    <template #item="{ element }">
                      <div>
                        <!-- Édition Inline -->
                        <div v-if="itemsEditing.includes(element.id)" class="px-4 py-2">
                          <form class="flex gap-2" @submit.prevent="updateItem(element)">
                            <UInput
                              v-model="editItemLabel[element.id]"
                              placeholder="Modifiez un item"
                              class="w-full"
                              @keydown.enter="updateItem(element)"
                            />
                            <UButton
                              type="submit"
                              icon="i-mdi-content-save"
                              size="sm"
                              color="primary"
                              :loading="itemsLoading.includes(element.id)"
                              @click="updateItem(element)"
                            />
                          </form>
                        </div>

                        <!-- Affichage régulier -->
                        <div
                          v-if="!itemsEditing.includes(element.id)"
                          class="relative flex items-center h-full print:text-sm py-2 print:py-0 print:ml-5 bg-gray-50"
                        >
                          <div class="flex flex-col md:flex-row gap-2 md:gap-0 px-0 md:px-0">
                            <div class="flex items-center justify-center w-7 cursor-grab active:cursor-grabbing handle print:hidden">
                              <UIcon name="i-mdi-drag-vertical" class="text-poivre-200 size-6" />
                            </div>
                            <div v-if="allowMultiselect" class="flex items-center justify-center w-7 md:mr-2 print:hidden">
                              <UCheckbox
                                :model-value="selectedItems.includes(element.id)"
                                @update:model-value="checked => {
                                  if (checked) {
                                    selectedItems.push(element.id)
                                  }
                                  else {
                                    selectedItems = selectedItems.filter(id => id !== element.id)
                                  }
                                }"
                              />
                            </div>
                          </div>
                          <div class="flex items-center flex-auto h-full min-w-0 print:py-1">
                            <button
                              v-if="element.flyer_item"
                              class="border-0 rounded-md overflow-hidden mr-2 h-12 w-12 block print:hidden flex-shrink-0"
                              :class="{ 'ring-1 ring-error ': element.flyer_item_id && isFlyerOfferExpired(element) }"
                              @click="promoDetailsId = element.flyer_item_id; promoModalOpen = true"
                            >
                              <img
                                :src="element.flyer_item.cdn_thumbnail_url || `${config.public.backendCdnUrl}/u/flyers/items/${element.flyer_item.flyer_id}/${element.flyer_item.image_file}`"
                                class="h-12 w-12"
                                :class="{ 'opacity-30': element.slashed === 1 || isFlyerOfferExpired(element) }"
                                @click="modalStore.openItemModal(element.flyer_item_id)"
                              >
                            </button>

                            <div class="flex flex-grow flex-col justify-center min-w-0">
                              <div class="text-poivre leading-5" style="font-size:15px;">
                                <span class="mr-2 hidden print:inline font-medium">
                                  <UIcon name="i-mdi-crop-square" class="inline" /> {{ element.qty }}x
                                </span>
                                <span class="cursor-pointer" :class="{ 'line-through': element.slashed === 1 }" @click="slash(element)">{{ element.label }}</span>
                                <span v-if="element.flyer_item" class="text-bleuet font-light ml-1">
                                  ({{ element.flyer_item.price_description }})
                                </span>
                                <UPopover
                                  v-if="element.added_by_info"
                                  :popper="{ placement: 'top' }"
                                  class="inline-flex align-middle"
                                  @click.stop
                                >
                                  <button
                                    type="button"
                                    class="ml-1 inline-flex items-center text-poivre-500 hover:text-poivre-700"
                                    @click.stop
                                  >
                                    <UIcon
                                      name="i-mdi-account-multiple"
                                      class="size-4"
                                    />
                                  </button>
                                  <template #content>
                                    <div class="px-2 py-1 text-sm">
                                      Ajouté par {{ element.added_by_info.name }}
                                    </div>
                                  </template>
                                </UPopover>
                              </div>

                              <div
                                v-if="element.recipe_id || canShowAislesOnItems"
                                class="flex gap-1 mt-1 print:hidden items-center min-w-0"
                              >
                                <div
                                  v-if="canShowAislesOnItems"
                                  class="min-w-0 flex-1"
                                >
                                  <UPopover
                                    v-model:open="aislePopoverOpen[element.id]"
                                    :popper="{ placement: 'bottom-start' }"
                                    class="block min-w-0 max-w-full"
                                  >
                                    <button
                                      class="flex items-center gap-1 min-w-0 max-w-full px-1.5 py-0.5 rounded-full text-xs font-semibold overflow-hidden transition-colors"
                                      :class="!getAisleMeta(element.aisle_id)
                                        ? 'ring-1 ring-inset ring-gray-300 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600 hover:ring-gray-400'
                                        : 'border border-solid bg-transparent opacity-100 hover:opacity-80'"
                                      :style="getAisleMeta(element.aisle_id) ? {
                                        borderColor: getDisplayAisleMeta(element.aisle_id)?.backgroundColor,
                                        color: getDisplayAisleMeta(element.aisle_id)?.textColor,
                                      } : {}"
                                    >
                                      <template v-if="!getAisleMeta(element.aisle_id)">
                                        <UIcon
                                          v-if="!isAisleItemLoading(element.id)"
                                          name="i-mdi-plus-circle-outline"
                                          class="size-3.5 shrink-0"
                                        />
                                        <span class="truncate min-w-0">{{ isAisleItemLoading(element.id) ? 'Détection…' : 'Choisir une allée' }}</span>
                                      </template>
                                      <span
                                        v-else
                                        class="truncate min-w-0"
                                      >{{ getDisplayAisleMeta(element.aisle_id)?.label }}</span>
                                      <span
                                        v-if="isAisleItemLoading(element.id)"
                                        class="inline-flex shrink-0 items-center justify-center w-3.5 h-3.5"
                                      >
                                        <UIcon
                                          name="i-mdi-loading"
                                          class="size-3.5 animate-spin"
                                        />
                                      </span>
                                    </button>
                                    <template #content>
                                      <div class="p-1 w-68 max-h-72 overflow-y-scroll">
                                        <button
                                          v-for="aisle in getSortedAisles()"
                                          :key="`aisle-option-${element.id}-${aisle.id}`"
                                          class="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm hover:bg-gray-100"
                                          @click="saveItemAisle(element, aisle.id)"
                                        >
                                          <UIcon
                                            v-if="aisle.id === OTHER_AISLE_ID"
                                            name="i-mdi-close"
                                            class="size-3.5 shrink-0 text-poivre-500"
                                          />
                                          <span
                                            v-else
                                            class="inline-block size-2.5 rounded-full shrink-0"
                                            :style="{ backgroundColor: aisle.backgroundColor }"
                                          />
                                          <span class="truncate">{{ aisle.label }}</span>
                                        </button>
                                      </div>
                                    </template>
                                  </UPopover>
                                </div>

                                <UPopover v-if="element.recipe_id">
                                  <button
                                    aria-label="Recette liée"
                                    title="Recette liée"
                                    class="inline-flex items-center gap-1 shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ring-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors"
                                  >
                                    <UIcon name="i-mdi-silverware-fork-knife" class="size-3.5 shrink-0" />
                                    <span class="hidden md:inline">Recette</span>
                                  </button>
                                  <template #content>
                                    <div class="p-3 bg-white rounded-lg shadow w-52 text-sm">
                                      <b>Lié à la recette:</b><br>{{ element.recipe.title }}
                                      <UButton
                                        label="Ouvrir la recette"
                                        class="mt-2"
                                        size="sm"
                                        :to="localePath({ name: 'RecipePage', params: { id: element.recipe.id, slug: element.recipe.slug } })"
                                      />
                                    </div>
                                  </template>
                                </UPopover>
                              </div>
                            </div>

                            <div class="flex items-center gap-0.5 ml-auto print:hidden">
                              <div class="flex border-1 rounded-full border-primary items-center">
                                <UButton
                                  v-if="element.qty > 1"
                                  icon="i-mdi-minus"
                                  size="xs"
                                  color="primary"
                                  @click="updateQuantity(element, 'minus')"
                                />
                                <UButton
                                  v-else
                                  icon="i-mdi-delete"
                                  size="xs"
                                  color="error"
                                  @click="deleteItem(element)"
                                />

                                <span class="text-sm font-semibold mx-1.5">{{ element.qty }}</span>

                                <UButton
                                  icon="i-mdi-plus"
                                  size="xs"
                                  color="primary"
                                  @click="updateQuantity(element, 'plus')"
                                />
                              </div>

                              <UDropdownMenu :items="getItemActions(element)">
                                <UButton
                                  icon="i-mdi-dots-vertical"
                                  size="xs"
                                  variant="ghost"
                                  color="secondary"
                                  class="mr-0.5"
                                  :ui="{ leadingIcon: 'size-5 shrink-0' }"
                                />
                              </UDropdownMenu>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </draggable>

                  <!-- Ajout d'un item -->
                  <div class="px-2 py-1 bg-poivre-50 border-t border-gray-200 flex items-center gap-2 print:hidden">
                    <form class="flex w-full space-x-2" @submit.prevent="addItem($event, list.id)">
                      <UInput v-model="newItemLabel[list.id]" :loading="listsLoading[list.id]" :disabled="listsLoading[list.id]" size="lg" class="w-full print:hidden" placeholder="Entrez un article..." :ui="{ trailing: 'pe-1' }" @keydown.enter="saveList()">
                        <template #trailing>
                          <UButton
                            color="primary"
                            variant="ghost"
                            type="submit"
                            icon="i-mdi-plus-circle"
                            :disabled="listsLoading[list.id]"
                            @click="saveList()"
                          />
                        </template>
                      </UInput>
                    </form>
                  </div>

                  <div
                    v-if="isListFrozen(list.id)"
                    class="absolute inset-0 bg-white/50 pointer-events-auto"
                    aria-hidden="true"
                  >
                    <div class="absolute inset-0 animate-pulse bg-poivre-100/30" />
                  </div>
                  </div>
                </div>
                <div
                  v-if="index % 2 === 1 && index < lists.length - 1 && baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
                  class="md:hidden"
                >
                  <DisplayAd
                    dfp-ad-unit-path="glouton/liste-epicerie"
                    :sizes="[[320, 50]]"
                    :size-mapping="[[[0, 0], [[320, 50]]], [[768, 0], [[0, 0]]]]"
                    custom-class=""
                  />
                </div>
              </template>
            </div>

            <!-- Aucune liste trouvée -->
            <div v-else class="text-center p-12 border-2 border-dashed border-poivre-300 rounded-lg">
              Aucune liste trouvée.
            </div>
          </div>
        </ClientOnly>
      </div>

      <div v-else class="py-6">
        <ShoppingListHelp />
      </div>
    </UContainer>

    <!-- Composant d'impression caché -->
    <PrintList ref="printListRef" :lists="lists" />
  </div>
</template>

<style scoped>
.extra-badge-gold {
  background: linear-gradient(135deg, #e5d9a2 0%, #f0e8be 40%, #f7f2df 50%, #f0e8be 60%, #e5d9a2 100%);
}
</style>
